import { Button, FormLabel, TextField } from '@tunasong/ui-lib'
import { getImageMimeTypes, usePlugins } from '@tunasong/plugin-lib'
import { features, OAuth, profilesApi, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { Entity, Persisted, Profile } from '@tunasong/schemas'
import { DropZone, HBox, Typography, UserAvatar, VBox } from '@tunasong/ui-lib'
import { isSupportChatEnabled } from '@tunasong/vendor'
import type { FC } from 'react'
import React, { useState } from 'react'
import { ContactSupportButton } from '../help/help-menu.js'
import styles from './account.module.css'

export interface AccountProps {
  className?: string
  profile: Persisted<Profile>
}

export const Account: FC<AccountProps> = props => {
  const { profile } = props

  const logout = () => {
    void OAuth.logout()
    dispatch(features.user.actions.signout())
  }

  const dispatch = useThunkDispatch()
  const user = useSelector(state => state.user)

  const [updateProfile] = profilesApi.useUpdateProfileMutation()

  const imageMimeTypes = getImageMimeTypes(usePlugins() ?? [])

  /** The name field is controlled, so an attempt to clear the name can put the last saved name back */
  const [name, setName] = useState(profile?.name ?? '')
  const [savedName, setSavedName] = useState(profile?.name ?? '')
  const [isEmptyNameRejected, setIsEmptyNameRejected] = useState(false)

  if (!profile) {
    return null
  }

  const aiTokens = profile.usage.aiTokens ? Math.round(profile.usage.aiTokens) : 0

  const storageMB = (profile.usage.storage ?? 0) / (1024 * 1024)
  const storageGB = storageMB / 1024
  const storageLabel = `${storageGB > 1 ? Math.round(storageGB) + ' GB' : Math.round(storageMB) + ' MB'}`

  const handleNameChange = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setName(ev.target.value)

  /** Bandmates see the name everywhere, so an empty name is never saved */
  const updateProfileName = () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setName(savedName)
      setIsEmptyNameRejected(true)
      return
    }
    setName(trimmed)
    setSavedName(trimmed)
    setIsEmptyNameRejected(false)
    void updateProfile({ id: profile.id, profile: { name: trimmed } })
  }
  const handleUpdatePicture = (picture: Persisted<Entity>) => {
    // Update the profile picture. This link will expire, but the server profile API will overwrite it.
    void updateProfile({ id: profile.id, profile: { picture: picture.storageUrls?.url } })
  }

  const handleUploading = () => {
    // Update the profile picture. This link will expire, but the server profile API will overwrite it.
    void updateProfile({ id: profile.id, profile: { picture: null } })
  }

  return (
    <VBox>
      <Typography variant="h6" className={styles.profileHeading}>
        Profile
      </Typography>

      <HBox className={styles.profileRow}>
        <DropZone
          className={styles.dropZone}
          parentId={profile.id}
          accept={imageMimeTypes}
          onUploaded={handleUpdatePicture}
          onUploading={handleUploading}
          uploadOnClick
        >
          <UserAvatar userId={user.userId} size="large" link={false} profileCard={false} aria-label="user" />
        </DropZone>
        <TextField
          label="My name"
          value={name}
          onChange={handleNameChange}
          maxLength={80}
          autoComplete="name"
          helperText={
            isEmptyNameRejected ? "Your name can't be empty. Bandmates see it on everything you share." : undefined
          }
          variant="standard"
          fullWidth
          onBlur={updateProfileName}
        />
        <Button className={styles.signOutButton} variant="outlined" onClick={logout}>
          Sign out
        </Button>
      </HBox>

      <Typography variant="h6">Account</Typography>

      <FormLabel>E-mail</FormLabel>
      <Typography>{user.email} </Typography>
      <Typography variant="caption">{user.userId}</Typography>

      <Typography variant="h6" className={styles.sectionHeading}>
        Identity Provider
      </Typography>
      <Typography> {user.authProvider}</Typography>

      <Typography variant="h6" className={styles.sectionHeading}>
        Storage
      </Typography>
      <Typography variant="body2">You uploaded {storageLabel} content.</Typography>

      <Typography variant="h6" className={styles.sectionHeading}>
        AI tokens
      </Typography>
      <Typography variant="body2">You have used {aiTokens} AI tokens.</Typography>

      {/* Contact support's home on mobile, which has no sidebar footer with Help */}
      {isSupportChatEnabled() ? (
        <>
          <Typography variant="h6" className={styles.sectionHeading}>
            Help
          </Typography>
          <ContactSupportButton />
        </>
      ) : null}
    </VBox>
  )
}

export default Account
